/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { I18nService } from '@core/i18n-legacy';
import { createEffectFactory } from '@core/ngrx-legacy';
import { TELLENT_INITIAL_CURRENT_USER, TellentMember } from '@core/tellent-types';
import { resolveMemberFullName, TellentComponentsNotificationService } from '@core/tellent-util';

import { TellentMembersHttpClient } from './tellent-members.http';
import { TellentMembersActions } from './tellent-members.store';
import { TellentCurrentUserEntityActions, TellentCurrentUserSelectors } from '../../current-user';

@Injectable({ providedIn: 'root' })
export class TellentMembersEffects {
  private readonly tellentMembersHttpClient = inject(TellentMembersHttpClient);
  private readonly actions$ = inject(Actions);
  private readonly notificationService = inject(TellentComponentsNotificationService);
  private readonly i18n = inject(I18nService);
  public store = inject(Store);
  public effects = createEffectFactory(TellentMembersActions, this.actions$);

  public inviteTellentMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.inviteTellentMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.inviteTellentMember(payload).pipe(
          map(data => TellentMembersActions.inviteTellentMemberSuccess({ payload: data })),
          catchError(error => {
            this.handleAddMembersFailure();
            return of(TellentMembersActions.inviteTellentMemberFail({ payload: error }));
          }),
        ),
      ),
    ),
  );

  public inviteExistingMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.inviteExistingMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.inviteExistingMember(payload).pipe(
          map(data => TellentMembersActions.inviteExistingMemberSuccess({ payload: data })),
          catchError(error => {
            this.handleAddMembersFailure();
            return of(TellentMembersActions.inviteExistingMemberFail({ payload: error }));
          }),
        ),
      ),
    ),
  );

  public inviteAppMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.inviteAppMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.inviteAppMember(payload).pipe(
          map(data => TellentMembersActions.inviteAppMemberSuccess({ payload: data })),
          catchError(error => {
            this.handleAddMembersFailure();
            return of(TellentMembersActions.inviteAppMemberFail({ payload: error }));
          }),
        ),
      ),
    ),
  );

  public fetchMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.fetchOne),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.fetchMemberByGuid(payload.id).pipe(
          map(response => TellentMembersActions.fetchOneSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.fetchOneFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public fetchMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.fetchPaginatedCollection),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.fetchMembers(payload.params).pipe(
          map(({ data, meta }) =>
            TellentMembersActions.fetchPaginatedCollectionSuccess({
              payload: {
                data: { rows: data, meta },
                scope: payload.params.scope,
                add: payload.add,
              },
            }),
          ),
          catchError(error =>
            of(TellentMembersActions.fetchPaginatedCollectionFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public fetchAppMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.fetchAppMembersPaginated),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.fetchAppMembers(payload.params).pipe(
          map(({ data, meta }) =>
            TellentMembersActions.fetchAppMembersPaginatedSuccess({
              payload: {
                data: { rows: data, meta },
                scope: payload.params.scope,
                add: payload.add,
              },
            }),
          ),
          catchError(error =>
            of(TellentMembersActions.fetchAppMembersPaginatedFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public deactivateMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.deactivateMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.deactivateMember(payload).pipe(
          tap(payload => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.success_toast.deactivate', {
                member: resolveMemberFullName(payload.data),
              }),
            );
          }),
          map(response => TellentMembersActions.deactivateMemberSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.deactivateMemberFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public bulkDeactivate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.bulkDeactivate),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.bulkDeactivateMembers(payload).pipe(
          map(response => {
            const ok = response.results.filter(r => r.status === 'ok');
            const failed = response.results.filter(r => r.status === 'error');
            if (ok.length) {
              this.notificationService.success(
                this.i18n.translate('tlc.members.success_toast.bulk_deactivate', {
                  count: ok.length,
                }),
              );
            }
            if (failed.length) {
              this.notificationService.error(
                this.i18n.translate('tlc.members.error_toast.bulk_deactivate_partial', {
                  count: failed.length,
                }),
              );
            }
            return TellentMembersActions.bulkDeactivateSuccess({
              payload: { data: ok.map(r => (<{ member: TellentMember }>r).member) },
            });
          }),
          catchError(error => of(TellentMembersActions.bulkDeactivateFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public bulkReactivate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.bulkReactivate),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.bulkReactivateMembers(payload).pipe(
          map(response => {
            const ok = response.results.filter(r => r.status === 'ok');
            const failed = response.results.filter(r => r.status === 'error');
            if (ok.length) {
              this.notificationService.success(
                this.i18n.translate('tlc.members.success_toast.bulk_reactivate', {
                  count: ok.length,
                }),
              );
            }
            if (failed.length) {
              this.notificationService.error(
                this.i18n.translate('tlc.members.error_toast.bulk_reactivate_partial', {
                  count: failed.length,
                }),
              );
            }
            return TellentMembersActions.bulkReactivateSuccess({
              payload: { data: ok.map(r => (<{ member: TellentMember }>r).member) },
            });
          }),
          catchError(error => of(TellentMembersActions.bulkReactivateFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public bulkResetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.bulkResetPassword),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.bulkResetPasswords(payload).pipe(
          map(response => {
            const ok = response.results.filter(r => r.status === 'ok');
            const failed = response.results.filter(r => r.status === 'error');
            if (ok.length) {
              this.notificationService.success(
                this.i18n.translate('tlc.members.success_toast.bulk_reset_password', {
                  count: ok.length,
                }),
              );
            }
            if (failed.length) {
              this.notificationService.error(
                this.i18n.translate('tlc.members.error_toast.bulk_reset_password_partial', {
                  count: failed.length,
                }),
              );
            }
            return TellentMembersActions.bulkResetPasswordSuccess();
          }),
          catchError(error =>
            of(TellentMembersActions.bulkResetPasswordFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public bulkResendInvitations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.bulkResendInvitations),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.bulkResendInvitations(payload).pipe(
          map(response => {
            const ok = response.results.filter(r => r.status === 'ok');
            const failed = response.results.filter(r => r.status === 'error');
            if (ok.length) {
              this.notificationService.success(
                this.i18n.translate('tlc.members.success_toast.bulk_resend_invitation', {
                  count: ok.length,
                }),
              );
            }
            if (failed.length) {
              this.notificationService.error(
                this.i18n.translate('tlc.members.error_toast.bulk_resend_invitation_partial', {
                  count: failed.length,
                }),
              );
            }
            return TellentMembersActions.bulkResendInvitationsSuccess();
          }),
          catchError(error =>
            of(TellentMembersActions.bulkResendInvitationsFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public bulkInviteViaEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.bulkInviteViaEmail),
      mergeMap(({ payload }) =>
        forkJoin(
          payload.payloads.map(p =>
            this.tellentMembersHttpClient.inviteExistingMember(p).pipe(
              map(() => ({ sent: p.members.length, failed: 0 })),
              catchError(() => of({ sent: 0, failed: p.members.length })),
            ),
          ),
        ).pipe(
          map(parts => {
            const sent = parts.reduce((n, p) => n + p.sent, 0);
            const failed = parts.reduce((n, p) => n + p.failed, 0);
            if (sent > 0) {
              this.notificationService.success(
                this.i18n.translate('tlc.members.success_toast.bulk_invite', { count: sent }),
              );
            }
            if (failed > 0) {
              this.notificationService.error(
                this.i18n.translate('tlc.members.error_toast.bulk_invite_partial', {
                  count: failed,
                }),
              );
            }
            return TellentMembersActions.bulkInviteViaEmailSuccess();
          }),
          catchError(error => of(TellentMembersActions.bulkInviteViaEmailFail({ payload: error }))),
        ),
      ),
    ),
  );

  public bulkGenerateLogins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.bulkGenerateLogins),
      mergeMap(({ payload }) =>
        forkJoin(
          payload.payloads.map(p => this.tellentMembersHttpClient.inviteExistingMember(p)),
        ).pipe(
          map(() => TellentMembersActions.bulkGenerateLoginsSuccess()),
          catchError(error => of(TellentMembersActions.bulkGenerateLoginsFail({ payload: error }))),
        ),
      ),
    ),
  );

  public deleteMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.deleteMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.deleteMember(payload).pipe(
          map(response => TellentMembersActions.deleteMemberSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.deactivateMemberFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public reactivateMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.reactivateMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.reactivateMember(payload).pipe(
          tap(payload => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.success_toast.reactivate', {
                member: resolveMemberFullName(payload.data),
              }),
            );
          }),
          map(response => TellentMembersActions.reactivateMemberSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.reactivateMemberFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public sendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.sendInvitation),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.sendInvitation(payload).pipe(
          tap(() => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.success_toast.invite'),
            );
          }),
          map(() => TellentMembersActions.sendInvitationSuccess({ payload })),
          catchError(error => of(TellentMembersActions.sendInvitationFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public manageSeats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.manageSeats),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.manageSeats(payload).pipe(
          tap(() => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.add_member.roles.success_message'),
            );
          }),
          map(response => TellentMembersActions.manageSeatsSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.manageSeatsFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public updateMemberData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.updateMember),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.updateMemberData(payload).pipe(
          map(response =>
            TellentMembersActions.updateMemberSuccess({
              payload: response,
            }),
          ),
          catchError(error => of(TellentMembersActions.updateMemberFail({ payload: error }))),
        ),
      ),
    ),
  );

  public addGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.addGroups),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.addGroups(payload).pipe(
          map(response =>
            TellentMembersActions.addGroupsSuccess({
              payload: response,
            }),
          ),
          catchError(error => of(TellentMembersActions.addGroupsFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public removeGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.removeGroups),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.removeGroups(payload).pipe(
          map(response =>
            TellentMembersActions.removeGroupsSuccess({
              payload: response,
            }),
          ),
          catchError(error => of(TellentMembersActions.removeGroupsFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public sendPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.sendPasswordReset),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.sendPasswordResetInstructions(payload.guid).pipe(
          tap(() => {
            this.notificationService.success(
              this.i18n.translate(
                'tlc.members.edit_member.account_tab.section.password.success_toast',
              ),
            );
          }),
          map(response => TellentMembersActions.sendPasswordResetSuccess()),
          catchError(error => of(TellentMembersActions.sendPasswordResetFail({ payload: error }))),
        ),
      ),
    ),
  );

  public updateAbilities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.updateAbilities),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.updateAbilities(payload.guid, payload.abilities).pipe(
          map(response => TellentMembersActions.updateAbilitiesSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.updateAbilitiesFail({ payload: error }))),
        ),
      ),
    ),
  );

  public refreshCurrentUserOnSeatChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.manageSeatsSuccess),
      withLatestFrom(
        this.store.select(TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe),
      ),
      filter(
        ([action, me]) =>
          !!action.payload.data.find(seat => me?.seats.find(({ guid }) => guid === seat.guid)),
      ),
      switchMap(() => [TellentCurrentUserEntityActions.fetch()]),
    ),
  );

  public changeLoginMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.changeLoginMethod),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.changeLoginMethod(payload).pipe(
          tap(() => {
            this.notificationService.success(
              this.i18n.translate(
                'tlc.members.edit_member.change_login_method_dialog.notification_success',
                { email: payload.email },
              ),
            );
          }),
          map(response => TellentMembersActions.changeLoginMethodSuccess({ payload: response })),
          catchError(error =>
            of(TellentMembersActions.changeLoginMethodFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public generateTellentPasswords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.generateTellentPasswords),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.generateTellentPasswords(payload).pipe(
          map(response =>
            TellentMembersActions.generateTellentPasswordsSuccess({ payload: response }),
          ),
          catchError(error =>
            of(TellentMembersActions.generateTellentPasswordsFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public generateAppPasswords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.generateAppPasswords),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.generateAppPasswords(payload).pipe(
          map(response => TellentMembersActions.generateAppPasswordsSuccess({ payload: response })),
          catchError(error =>
            of(TellentMembersActions.generateAppPasswordsFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public ngrxOnInitEffects() {
    return TellentMembersActions.fetchCollectionSuccess({
      payload: { data: inject(TELLENT_INITIAL_CURRENT_USER).members || [] },
    });
  }

  private handleAddMembersFailure() {
    this.notificationService.error(this.i18n.translate('tlc.common.errors.unknown_error'));
  }
}
